import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {AvailableDomain} from '../../../../../../models/cms/available-domain';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router';
import {AvailableDomainService} from '../../../../../../services/cms/available-domain.service';
import {StaticPage} from '../../../../../../models/cms/static-page';
import {StaticPageService} from '../../../../../../services/cms/static-page.service';
import {debounceTime, distinctUntilChanged, forkJoin, Subject} from 'rxjs';
import {StaticPageImageService} from '../../../../../../services/cms/static-page-image.service';
import {StaticPageImage} from '../../../../../../models/cms/static-page-image';
import {StaticPageStatus} from '../../../../../../constants/static-page-status';
import {CopyToClipboardComponent} from '../../../../../layout/copy-to-clipboard/copy-to-clipboard.component';
import {CodeEditor} from '@acrodata/code-editor';
import {html} from '@codemirror/lang-html';
import {UploadFileService} from '../../../../../../services/upload-file/upload-file.service';
import {UploadFile} from '../../../../../../models/upload-file';
import {oneDark} from '@codemirror/theme-one-dark';
import {EditorView} from '@codemirror/view';
import {DatePipe, NgOptimizedImage} from '@angular/common';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {WidgetService} from '../../../../../../services/cms/widget.service';
import {AbstractWidget} from '../../../../../../models/cms/widget/abstract-widget';
import {ClipboardService} from '../../../../../../services/clipboard/clipboard.service';
import {AssetService} from '../../../../../../services/cms/asset.service';
import {Asset} from '../../../../../../models/cms/asset';
import {DocumentService} from '../../../../../../services/document/document.service';
import {ProductService} from '../../../../../../services/product/product.service';
import {Product} from '../../../../../../models/product';
import {PageTypeService} from '../../../../../../services/page-type/page-type.service';
import {PageType} from '../../../../../../models/page-type';
import {DropdownSelectComponent} from '../../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {LangService} from '../../../../../../services/language/lang.service';
import {
  StaticPageViewerComponent
} from '../../../../../layout/cms/static-page/static-page-viewer/static-page-viewer.component';
import {DialogService} from '../../../../../../services/dialog/dialog.service';
import {
  WidgetPreviewModalComponent
} from '../../../../../layout/cms/widget/widget-preview-modal/widget-preview-modal.component';
import {Scope} from '../../../../../../constants/scope';
import {ElementLoaderComponent} from '../../../../../layout/element-loader/element-loader.component';

@Component({
  selector: 'app-static-page-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    CopyToClipboardComponent,
    CodeEditor,
    FormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage,
    StaticPageViewerComponent,
    ElementLoaderComponent
  ],
  providers: [DatePipe],
  templateUrl: './static-page-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './static-page-form.component.scss'
})
@IsGranted(['STATIC_PAGE_VIEW'])
@MetaTitle('Page Builder')
export class StaticPageFormComponent implements OnInit {
  loaded = false;
  id: number | null = null;
  staticPage: StaticPage | null = null;
  staticPageImages: StaticPageImage[] = [];
  availableDomains: AvailableDomain[] = [];
  widgets: AbstractWidget[] = [];
  assets: Asset[] = [];
  assetName: string = '';
  assetNameChange: Subject<string> = new Subject<string>();
  assetTypeFilter: string[] = [];
  selectedAsset: Asset | null = null;
  assetImageWidth: string = '100%';
  assetImageHeight: string = 'auto';
  assetVideoWidth: string = '100%';
  assetVideoHeight: string = 'auto';
  assetVideoAutoplay: boolean = false;
  assetVideoMuted: boolean = true;
  assetVideoControls: boolean = true;
  assetVideoLoop: boolean = false;
  widgetName: string = '';
  widgetNameChange: Subject<string> = new Subject<string>();
  products: Product[] = [];
  pageTypes: PageType[] = [];
  locales: string[] = [];
  availableLocales: string[] = [];
  widgetProductId: number[] = [];
  widgetLang: string[] = [];
  availableDomainLoading = false;

  StaticPageStatus = StaticPageStatus;
  extensions = [
    html(),
    oneDark,
    EditorView.updateListener.of(update => {
      if (update.selectionSet || update.focusChanged) {
        this.lastEditorCursor = update.state.selection.main.head;
      }
    }),
  ];

  @ViewChild(CodeEditor) codeEditor!: CodeEditor;
  @ViewChild(StaticPageViewerComponent) staticPageViewer!: StaticPageViewerComponent;

  lastEditorCursor: number | null = null;

  isDragOver = false;
  uploadProgress = 0;
  isUploading = false;
  totalCount = 0;
  uploadedCount = 0;

  tab: 'assets' | 'widgets' | 'preview' | 'content-library' = 'assets';

  private hideProgressTimeoutId: number | null = null;

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    pageTypeId: new FormControl<number | null>(null),
    productId: new FormControl<number | null>(null),
    domainId: new FormControl<number | null>(null),
    slug: new FormControl<string | null>(null),
    locale: new FormControl<string | null>(null),
    htmlContent: new FormControl<string | null>(null),
    status: new FormControl<StaticPageStatus>(StaticPageStatus.PENDING, Validators.required),
    scope: new FormControl(Scope.PUBLIC, Validators.required),
    publishedAt: new FormControl<string | null>(null),
  });

  constructor(
    protected langService: LangService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private staticPageService: StaticPageService,
    private staticPageImageService: StaticPageImageService,
    private availableDomainService: AvailableDomainService,
    private uploadFileService: UploadFileService,
    private widgetService: WidgetService,
    private assetService: AssetService,
    private documentService: DocumentService,
    private clipboardService: ClipboardService,
    private productService: ProductService,
    private pageTypeService: PageTypeService,
    private datePipe: DatePipe,
    private dialogService: DialogService,
  ) {
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params: ParamMap) => {
      this.id = params.get('id') === 'add' ? null : Number(params.get('id'));

      this.loader.startLoading();

      forkJoin([
        this.pageTypeService.findAll({sort: 'name'}),
        this.productService.findAll({sort: 'name'}),
      ]).subscribe(([pageTypes, products]) => {
        this.pageTypes = pageTypes;
        this.products = products;

        if (this.id) {
          forkJoin([
            this.staticPageService.findOneById(this.id, {groups: 'htmlContent'}),
            this.staticPageImageService.findAll({staticPageId: this.id})
          ]).subscribe(([staticPage, staticPageImages]) => {
            this.staticPage = staticPage as StaticPage;
            this.staticPageImages = staticPageImages;

            this.form.patchValue({
              name: this.staticPage.name,
              pageTypeId: this.staticPage.pageType?.id ?? null,
              productId: this.staticPage.product?.id ?? null,
              domainId: this.staticPage.domain?.id ?? null,
              slug: this.staticPage.slug,
              locale: this.staticPage.locale,
              htmlContent: this.staticPage.htmlContent,
              status: this.staticPage.status,
              scope: this.staticPage.scope,
              publishedAt: this.staticPage.publishedAt ? this.datePipe.transform(this.staticPage.publishedAt, 'yyyy-MM-dd HH:mm') : null
            });

            this.form.get('htmlContent')!.valueChanges.subscribe(value => {
              if (this.staticPage) {
                this.staticPage.htmlContent = value ?? null;
              }
            });

            this.refreshLocales();

            this.loader.stopLoading();
            this.loaded = true;
            this.findWidgets();
            this.findAssets();
          });
        } else {
          this.refreshLocales();

          this.loader.stopLoading();
          this.loaded = true;
        }
      });
    });

    this.widgetNameChange
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((model: string) => {
        this.widgetName = model;
        this.findWidgets();
      });

    this.assetNameChange
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((model: string) => {
        this.assetName = model;
        this.findAssets();
      });
  }

  getSelectedProduct(): Product | null {
    const productId = this.form.get('productId')?.value ?? null;

    if (productId) {
      return this.products.find((product: Product) => product.id === productId) ?? null;
    }

    return null;
  }

  getSelectedLocale(): string | null {
    return this.form.get('locale')?.value ?? null;
  }

  refreshLocales(): void {
    const product = this.getSelectedProduct();
    this.availableLocales = product?.languages ?? [];

    if (this.availableLocales.length) {
      this.form.get('locale')?.enable();
    } else {
      this.form.get('locale')?.disable();
      this.form.get('locale')?.setValue(null);
    }

    this.locales = this.availableLocales;

    this.refreshDomains();
  }

  refreshDomains(): void {
    this.availableDomains = [];

    const productId = this.getSelectedProduct()?.id ?? null;
    const lang = this.getSelectedLocale();

    if (productId === null || lang === null) {
      this.form.get('domainId')?.disable();
      this.form.get('domainId')?.setValue(null);
      return;
    }

    this.form.get('domainId')?.enable();

    const params = {
      sort: 'domain',
      productId: productId,
      lang: lang
    };

    this.availableDomainLoading = true;
    this.availableDomainService.findAll(params).subscribe((availableDomains: AvailableDomain[]) => {
      this.availableDomains = availableDomains;
      this.availableDomainLoading = false;
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    if (this.isCreate()) {
      this.staticPageService.create({
        name: this.form.value.name,
        pageTypeId: this.form.value.pageTypeId,
        productId: this.form.value.productId,
        locale: this.form.value.locale,
        domainId: this.form.value.domainId,
      }).subscribe((response) => {
        this.loader.stopLoading();

        this.router.navigateByUrl('/promotion-tools/cms/static-pages/' + response.id).then(() => {
          this.snackbar.success('The page has been added, you can now configure it.');
        });
      });
    } else if (this.staticPage?.id) {
      const data: any = {...this.form.getRawValue()};
      if (data.publishedAt) {
        const publishedAt = this.datePipe.transform(data.publishedAt, 'yyyy-MM-dd HH:mm:00');
        if (publishedAt) {
          data.publishedAt = new Date(publishedAt).toISOString()
        } else {
          data.publishedAt = null;
        }
      } else {
        data.publishedAt = null;
      }

      this.staticPageService.update(this.staticPage.id, data).subscribe(() => {
        this.loader.stopLoading();
        this.router.navigateByUrl('/promotion-tools/cms/static-pages').then(() => {
          this.snackbar.success('The page has been modified.');
        });
      });
    }
  }

  isCreate(): boolean {
    return !this.id;
  }

  getImageUrl(staticPageImage: StaticPageImage): string {
    let domain: string | null = this.staticPage?.domain?.domain ?? null;
    if (!domain && this.availableDomains.length) {
      if (this.form.value.domainId) {
        domain = this.availableDomains.find((availableDomain: AvailableDomain) => availableDomain.id === this.form.value.domainId)?.domain ?? null;
      } else {
        domain = this.availableDomains[0].domain ?? null;
      }
    }
    if (!domain) {
      return '';
    }

    return 'https://' + domain + '/static-assets/' + staticPageImage.document.id;
  }

  deleteImage(staticPageImage: StaticPageImage): void {
    if (staticPageImage.id) {
      this.staticPageImageService.delete(staticPageImage.id).subscribe(() => {
        this.staticPageImageService.findAll({staticPageId: this.id ?? 0}).subscribe((staticPageImages: StaticPageImage[]) => {
          this.staticPageImages = staticPageImages;

          this.snackbar.success('Image deleted successfully.');
        });
      });
    }
  }

  autoReplace(staticPageImage: StaticPageImage): void {
    const ctrl = this.form.get('htmlContent');
    if (!ctrl) {
      return;
    }

    const htmlContent = ctrl.value;
    const url = this.getImageUrl(staticPageImage);

    if (!htmlContent || !url) {
      return;
    }

    const fileName = staticPageImage.document.filename;
    if (!fileName) {
      return;
    }

    const result = this.replaceFileOccurrences(htmlContent, fileName, url);

    if (result.count === 0) {
      this.snackbar.warning(`"${fileName}" not found in the page.`);
      return;
    }

    setTimeout(() => {
      ctrl.setValue(result.content, {emitEvent: true});
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity({emitEvent: true});

      this.snackbar.success(`${result.count} occurrence(s) of "${fileName}" replaced.`);
    }, 10);
  }

  autoReplaceAll(): void {
    const ctrl = this.form.get('htmlContent');
    if (!ctrl || !ctrl.value) {
      return;
    }

    let content = ctrl.value;
    let totalCount = 0;
    let fileCount = 0;

    for (const staticPageImage of this.staticPageImages) {
      const url = this.getImageUrl(staticPageImage);
      const fileName = staticPageImage.document.filename;

      if (!url || !fileName) {
        continue;
      }

      const result = this.replaceFileOccurrences(content, fileName, url);

      if (result.count > 0) {
        content = result.content;
        totalCount += result.count;
        fileCount++;
      }
    }

    if (totalCount === 0) {
      this.snackbar.warning('No filename found to replace in the page.');
      return;
    }

    setTimeout(() => {
      ctrl.setValue(content, {emitEvent: true});
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity({emitEvent: true});

      this.snackbar.success(`${totalCount} occurrence(s) replaced for ${fileCount} file(s).`);
    }, 10);
  }

  private replaceFileOccurrences(content: string, fileName: string, url: string): { content: string, count: number } {
    const escapedFileName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let count = 0;

    // Quoted occurrences: HTML attributes (src="...", href="...") and JS strings ('...', "...", `...`)
    let next = content.replace(
      new RegExp(`(["'\`])(?:\\./)?${escapedFileName}\\1`, 'g'),
      (_m, quote: string) => {
        count++;
        return quote + url + quote;
      }
    );

    // Unquoted CSS url(...)
    next = next.replace(
      new RegExp(`url\\(\\s*(?:\\./)?${escapedFileName}\\s*\\)`, 'g'),
      () => {
        count++;
        return `url(${url})`;
      }
    );

    return {content: next, count};
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const files = Array.from(event.dataTransfer?.files ?? []);
    this.uploadFilesSequentially(files);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.uploadFilesSequentially(files);
  }

  copyWidgetTag(widget: AbstractWidget): void {
    const tag = `<widget widget-id="${widget.id}"></widget>`;
    this.clipboardService.copy(tag);
    this.snackbar.success(`Widget "${widget.name}" copied to clipboard.`);
  }

  viewWidget(widget: AbstractWidget): void {
    this.dialogService.openDialog(WidgetPreviewModalComponent, {data: {widget}}).subscribe();
  }

  insertWidgetTag(widget: AbstractWidget): void {
    const tag = `<widget widget-id="${widget.id}"></widget>`;
    const view = this.codeEditor?.view;

    if (view && this.lastEditorCursor !== null) {
      const cursor = this.lastEditorCursor;
      view.dispatch({
        changes: {from: cursor, insert: tag},
        selection: {anchor: cursor + tag.length},
      });
      view.focus();
      this.snackbar.success(`Widget "${widget.name}" inserted.`);
    } else {
      this.snackbar.warning(`Place your cursor in the editor first.`);
    }
  }

  switchTab(tab: 'assets' | 'widgets' | 'preview' | 'content-library'): void {
    this.tab = tab;
    if (tab === 'preview') {
      setTimeout(() => this.staticPageViewer?.refresh());
    }
  }

  onWidgetProductChange(): void {
    if (this.widgetProductId.length === 0) {
      this.locales = this.availableLocales;
    } else {
      const allLangs = new Set<string>();
      for (const product of this.products) {
        if (this.widgetProductId.includes(product.id!)) {
          for (const lang of product.languages) {
            allLangs.add(lang);
          }
        }
      }
      this.locales = [...allLangs];
    }

    this.widgetLang = this.widgetLang.filter(l => this.locales.includes(l));
    this.findWidgets();
  }

  onWidgetLangChange(): void {
    this.findWidgets();
  }

  getAssetUrl(asset: Asset): string {
    if (!asset.document?.id) return '';

    let domain: string | null = this.staticPage?.domain?.domain ?? null;
    if (!domain && this.availableDomains.length) {
      if (this.form.value.domainId) {
        domain = this.availableDomains.find((d: AvailableDomain) => d.id === this.form.value.domainId)?.domain ?? null;
      } else {
        domain = this.availableDomains[0]?.domain ?? null;
      }
    }
    if (!domain) return '';

    return 'https://' + domain + '/static-assets/' + asset.document.id;
  }

  selectAsset(asset: Asset): void {
    this.selectedAsset = asset;
  }

  insertAsset(asset: Asset): void {
    const type = asset.type;

    if (type === 'html') {
      if (!asset.document?.id) {
        this.snackbar.warning('This asset has no document.');
        return;
      }
      this.documentService.getContent(asset.document.id).subscribe({
        next: (result) => {
          const htmlContent = atob(result.content);
          this.insertAtCursor(htmlContent);
          this.selectedAsset = null;
        },
        error: () => {
          this.snackbar.warning('Failed to load asset content.');
        },
      });
      return;
    }

    const url = this.getAssetUrl(asset);

    if (!url) {
      this.snackbar.warning('This asset has no document or no domain is configured.');
      return;
    }

    let tag = '';

    if (type === 'image') {
      const w = this.assetImageWidth || 'auto';
      const h = this.assetImageHeight || 'auto';
      tag = `<img src="${url}" alt="${asset.name}" width="${w}" height="${h}">`;
    } else if (type === 'video') {
      const w = this.assetVideoWidth || 'auto';
      const h = this.assetVideoHeight || 'auto';
      const attrs = [];
      if (this.assetVideoControls) attrs.push('controls');
      if (this.assetVideoAutoplay) attrs.push('autoplay');
      if (this.assetVideoMuted) attrs.push('muted');
      if (this.assetVideoLoop) attrs.push('loop');
      tag = `<video src="${url}" width="${w}" height="${h}" ${attrs.join(' ')}></video>`;
    } else if (type === 'css') {
      tag = `<link rel="stylesheet" href="${url}">`;
    } else if (type === 'js') {
      tag = `<script src="${url}" defer></script>`;
    }

    if (!tag) return;

    if (type === 'css' || type === 'js') {
      if (this.isAssetAlreadyInserted(url)) {
        this.snackbar.warning('This asset is already included.');
        return;
      }
      this.insertInHead(tag);
    } else {
      this.insertAtCursor(tag);
    }

    this.selectedAsset = null;
  }

  private isAssetAlreadyInserted(url: string): boolean {
    const content = this.form.get('htmlContent')?.value ?? '';
    return content.includes(url);
  }

  private insertAtCursor(tag: string): void {
    const view = this.codeEditor?.view;

    if (view && this.lastEditorCursor !== null) {
      const cursor = this.lastEditorCursor;
      view.dispatch({
        changes: {from: cursor, insert: tag},
        selection: {anchor: cursor + tag.length},
      });
      view.focus();
      this.snackbar.success('Asset inserted.');
    } else {
      this.snackbar.warning('Place your cursor in the editor first.');
    }
  }

  private insertInHead(tag: string): void {
    const ctrl = this.form.get('htmlContent');
    if (!ctrl) return;

    let content = ctrl.value ?? '';

    const headCloseIndex = content.indexOf('</head>');
    if (headCloseIndex !== -1) {
      content = content.slice(0, headCloseIndex) + '  ' + tag + '\n' + content.slice(headCloseIndex);
    } else {
      const headOpenIndex = content.indexOf('<head>');
      if (headOpenIndex !== -1) {
        const insertPos = headOpenIndex + '<head>'.length;
        content = content.slice(0, insertPos) + '\n  ' + tag + content.slice(insertPos);
      } else {
        content = tag + '\n' + content;
      }
    }

    ctrl.setValue(content, {emitEvent: true});
    ctrl.markAsDirty();
    this.snackbar.success('Asset inserted in head.');
  }

  onAssetTypeChange(): void {
    this.findAssets();
  }

  private findAssets(): void {
    const params: any = {count: 20};
    if (this.assetName) {
      params.name = this.assetName;
    }
    if (this.assetTypeFilter.length > 0) {
      params.type = this.assetTypeFilter;
    }
    if (this.staticPage?.product?.id) {
      params.productId = this.staticPage.product.id;
    }
    if (this.staticPage?.locale) {
      params.lang = this.staticPage.locale;
    }
    this.assetService.findAll(params).subscribe((assets: Asset[]) => {
      this.assets = assets;
    });
  }

  private findWidgets(): void {
    const params: any = {restrictToUser: 1, count: 12, groups: 'site'};
    if (this.widgetName) {
      params.name = this.widgetName;
    }
    if (this.widgetProductId.length > 0) {
      params.productId = this.widgetProductId;
    }
    if (this.widgetLang.length > 0) {
      params.lang = this.widgetLang;
    }
    this.widgetService.findAll(params).subscribe((widgets: AbstractWidget[]) => {
      this.widgets = widgets;
    });
  }

  private uploadFilesSequentially(files: File[]): void {
    const filtered = files.filter(f => f && f.size > 0);
    if (filtered.length === 0) return;

    if (this.hideProgressTimeoutId !== null) {
      window.clearTimeout(this.hideProgressTimeoutId);
      this.hideProgressTimeoutId = null;
    }

    this.isUploading = true;
    this.totalCount = filtered.length;
    this.uploadedCount = 0;
    this.uploadProgress = 0;

    this.uploadOneByOne(filtered, 0);
  }

  private uploadOneByOne(files: File[], index: number): void {
    if (index >= files.length) {
      this.uploadProgress = 100;

      this.staticPageImageService
        .findAll({staticPageId: this.id})
        .subscribe((staticPageImages: StaticPageImage[]) => {
          this.staticPageImages = staticPageImages;
        });

      this.hideProgressTimeoutId = window.setTimeout(() => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.totalCount = 0;
        this.uploadedCount = 0;
        this.hideProgressTimeoutId = null;
      }, 2000);

      return;
    }

    const file = files[index];

    this.uploadFileService.upload(file).subscribe({
      next: (document: UploadFile) => {
        this.staticPageImageService.create({staticPageId: this.id, document}).subscribe({
          next: () => this.onOneFileDone(files, index),
          error: () => this.onOneFileDone(files, index)
        });
      },
      error: () => this.onOneFileDone(files, index)
    });
  }

  private onOneFileDone(files: File[], index: number): void {
    this.uploadedCount++;
    this.uploadProgress = Math.round((this.uploadedCount / this.totalCount) * 100);
    this.uploadOneByOne(files, index + 1);
  }
}
